import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";

import { useManualPoller } from "~/components/siteSpeed/hooks";
import ButtonWithSpinner from "~/components/ui/button/Button";

import { DnsConfig } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import { useWizard } from "../../../contexts/WizardContext";
import styles from "./Wizzard.module.scss";
import { WordpressPluginInstall } from "./WordpressPluginInstall";

interface InstallProps {
  data?: DnsConfig | undefined;
  isWizzard?: boolean;
}

interface RouteParams {
  domainId: string;
  [key: string]: string | undefined;
}

const POLLING_INTERVAL = 5000;

const Install = ({ data, isWizzard = false }: InstallProps) => {
  const { updateDomain, domain } = useWizard();
  const { domainId } = useParams<RouteParams>();

  const verificationKey = data?.[0] ? String(data[0][3]) : "";

  const { mutate: checkStatus } = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.domains.statusDetail(Number(domainId));

      return data?.domain;
    },
    onSuccess: async (domain) => {
      if (domain?.status?.install) {
        enqueueSnackbar("Domain verified successfully!", {
          variant: "success",
        });
        updateDomain(domain, { navigateToNextStep: true });
        stopPolling();
      }
    },
    onError: () => {
      stopPolling();
      enqueueSnackbar("Failed to check verification status", {
        variant: "error",
        persist: true,
      });
    },
  });

  const {
    start: startPolling,
    stop: stopPolling,
    isPolling,
  } = useManualPoller(async () => {
    await checkStatus();
  }, POLLING_INTERVAL);

  const handleDone = () => {
    startPolling();
  };

  return (
    <WordpressPluginInstall
      verificationKey={verificationKey}
      distinct={domain?.distinct}
      domainName={domain?.name}
      footer={
        isWizzard ? (
          <div className={styles.stepContentFooter}>
            <div />
            <ButtonWithSpinner
              onClick={handleDone}
              disabled={isPolling}
              withSpinner={isPolling}
            >
              {isPolling ? "Verifying" : "Done"}
            </ButtonWithSpinner>
          </div>
        ) : undefined
      }
    />
  );
};

export default Install;
